import classcat from "classcat";
import React from "react";

import { Button } from "@touchtech/web-baselayer-ui";

import BasketItems from "@is/components/BasketItems";
import DrawerActionButton from "@is/components/DrawerActionButton";
import { ActionButtonVariant } from "@is/components/DrawerActionButton/DrawerActionButton";
import {
  CHECKOUT_CONFIGURATIONS_TYPE_ICONS,
  CheckoutConfigurationType,
  DELIVERY_OPTIONS,
} from "@is/constants";

import Agreements from "../Agreements";
import CheckoutForm from "../CheckoutForm";
import DeliveryInformation from "../DeliveryInformation";
import DeliveryOptionsSwitcher from "../DeliveryOptionsSwitcher";
import ExternalPage from "../ExternalPage";
import FailedConfirmationModal from "../FailedConfirmationModal";
import css from "./CheckoutDrawerContent.module.css";
import useManageCheckoutDrawerContent from "./hooks/useManageCheckoutDrawerContent";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onEditQuantity: () => void;
  checkoutConfigurationsType: CheckoutConfigurationType;
};

const CheckoutDrawerContent = ({
  isOpen,
  onClose,
  onEditQuantity,
  checkoutConfigurationsType,
}: Props) => {
  const {
    containerRef,
    editingMode,
    isScrolled,
    showExternalPage,
    showFieldsErrorMessage,
    showPaymentErrorMessage,
    fieldsValidation,
    handleFormFieldChange,
    handleCompleteOrder,
    handleContinueMouseDown,
    handleContinueMouseUp,
    handleContinueMouseLeave,
    handleContinueTouchEnd,
    handleContinueTouchCancel,
    handleConfirmOrder,
    handleToggleCheckbox,
    handleEdit,
    handleScroll,
    handleOpenExternalPage,
    handleCloseExternalPage,
    isFailedModalOpen,
    setIsFailedModalOpen,
    isConfirmDisabled,
    isCheckoutPending,
    externalPageSrc,
    checkoutAgreements,
    handleCloseDrawer,
    formStateRecipientOptions,
    formStateDeliveryOptions,
    phoneOrEmail,
    handlePhoneOrEmailChange,
    handleSaveCheckoutInfo,
    checkoutConfigurationLabel,
    passedAllValidations,
    isValidationPending,
    errorMessage,
    setErrorMessage,
    handleToggleDeliveryOptions,
    activeDeliveryOption,
    t,
    storeName,
    storeAddress,
    areHomeDeliveryAndInstorePickupOptionsEnabled,
    isInstorePickupOptionSelected,
  } = useManageCheckoutDrawerContent({
    onClose,
    onEditQuantity,
    checkoutConfigurationsType,
    isOpen,
  });

  return (
    <>
      {showExternalPage ? (
        <ExternalPage
          handleClose={handleCloseExternalPage}
          src={externalPageSrc}
        />
      ) : (
        <>
          <DrawerActionButton
            onClick={handleEdit}
            isScrolled={isScrolled}
            variant={ActionButtonVariant.Back}
            className={css.backButton}
          />
          <DrawerActionButton
            onClick={handleCloseDrawer}
            isScrolled={isScrolled}
          />
          <div className={css.container} onScroll={handleScroll}>
            <div ref={containerRef} />
            {editingMode ? (
              <>
                <div className={css.header}>
                  <div className={classcat(["text-headline-5xl", css.title])}>
                    <strong>{t("Checkout")}</strong>
                  </div>
                  <BasketItems isCheckout />
                </div>
                {areHomeDeliveryAndInstorePickupOptionsEnabled && (
                  <DeliveryOptionsSwitcher
                    deliveryOptions={DELIVERY_OPTIONS}
                    onToggleDeliveryOptions={handleToggleDeliveryOptions}
                    activeDeliveryOption={activeDeliveryOption}
                  />
                )}
                <div className={css.checkoutFormContainer}>
                  <CheckoutForm
                    onChange={handleFormFieldChange}
                    onSaveCheckoutInfo={handleSaveCheckoutInfo}
                    validation={fieldsValidation}
                    deliveryOptions={formStateDeliveryOptions}
                    recipientOptions={formStateRecipientOptions}
                    phoneOrEmailField={phoneOrEmail}
                    onPhoneOrEmailChange={handlePhoneOrEmailChange}
                    activeDeliveryOption={activeDeliveryOption}
                    storeName={storeName}
                    storeAddress={storeAddress}
                  />
                  <div className={css.errorMessageContainer}>
                    {showFieldsErrorMessage && (
                      <span className="text-label-base">
                        {t("Please complete all required fields")}
                      </span>
                    )}
                  </div>
                  <div className={css.checkoutActionButtonsContainer}>
                    <Button
                      onMouseDown={handleContinueMouseDown}
                      onMouseUp={handleContinueMouseUp}
                      onMouseLeave={handleContinueMouseLeave}
                      onTouchEnd={handleContinueTouchEnd}
                      onTouchCancel={handleContinueTouchCancel}
                      onClick={handleCompleteOrder}
                      sizing="lg"
                      variant="primary"
                      className={classcat([
                        css.primaryButton,
                        {
                          [css.disabledButton]: !passedAllValidations,
                        },
                      ])}
                      state={isValidationPending ? "pending" : undefined}
                    >
                      {t("Continue")}
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div
                  className={classcat([
                    "text-headline-5xl",
                    css.title,
                    css.summary,
                  ])}
                >
                  <strong>{t("Summary")}</strong>
                </div>
                <DeliveryInformation
                  deliveryOptions={formStateDeliveryOptions}
                  recipientOptions={formStateRecipientOptions}
                  checkoutConfigurationsType={checkoutConfigurationsType}
                  storeName={storeName}
                  storeAddress={storeAddress}
                  isInstorePickupOptionSelected={isInstorePickupOptionSelected}
                />
                <Agreements
                  checkoutAgreements={checkoutAgreements}
                  onOpenExternalPage={handleOpenExternalPage}
                  onToggleCheckbox={handleToggleCheckbox}
                  showPaymentErrorMessage={showPaymentErrorMessage}
                />
                <div className={css.errorMessageContainer}>
                  {showPaymentErrorMessage && (
                    <span className="text-label-base">
                      {t("Please accept the payment condition")}
                    </span>
                  )}
                </div>
                <div className={css.checkoutActionButtonsContainer}>
                  <Button
                    onClick={handleConfirmOrder}
                    sizing="lg"
                    variant="primary"
                    state={isCheckoutPending ? "pending" : undefined}
                    className={classcat({
                      [css.primaryButton]: !isConfirmDisabled,
                      [css.disabledButton]: isConfirmDisabled,
                    })}
                  >
                    <div className={css.tickIcon}>
                      {
                        CHECKOUT_CONFIGURATIONS_TYPE_ICONS[
                          checkoutConfigurationsType
                        ]
                      }
                    </div>
                    {`${t("Confirm")} & ${t(checkoutConfigurationLabel)}`}
                  </Button>
                </div>
                <FailedConfirmationModal
                  isOpen={isFailedModalOpen}
                  setIsOpen={setIsFailedModalOpen}
                  errorMessage={errorMessage}
                  onSetErrorMessage={setErrorMessage}
                />
              </>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default CheckoutDrawerContent;
